import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { userCourseList_ } from '../../../slices/reportSlice';
import { decrypt, encrypt } from '../../../common/enc_dec';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Loader from '../../../components/loader';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const UserCourses = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = location?.state || {};
    const queryParams = new URLSearchParams(location.search);
    const _id = queryParams.get("id") ? decrypt(queryParams.get("id")) : null;
    const userId = id || _id;

    const { userCourseList, loading } = useSelector((state) => state.report);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [filter, setFilter] = useState("");
    const [completionFilter, setCompletionFilter] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (userId) {
            dispatch(userCourseList_({
                id: userId,
                is_random_test: false,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page,
                limit,
                is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined // Add completion filter
            }));
        }
    }, [dispatch, userId, filter, page, limit, startDate, endDate, completionFilter]);

    const handleViewCourse = (row) => {
        navigate(`/course/view?id=${encrypt(`${row.course_id}`)}`, { state: { courseData: row.user_course } });
    };

    // Calculate completed and not completed counts
    const completedCount = userCourseList?.data?.filter(course => course.is_test_completed)?.length || 0;
    const notCompletedCount = (userCourseList?.data?.length || 0) - completedCount;
    const totalCourses = userCourseList?.paging?.total || 0;

    const columns = [
        // Existing columns remain the same
        {
            name: "Sr No",
            selector: (row, index) => (page - 1) * limit + index + 1,
            sortable: false,
            width: "80px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Course ID",
            selector: (row) => row.course_id || "NA",
            sortable: true,
            width: "100px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Course Name",
            selector: (row) => (
                <span className="text-capitalize">{row.user_course?.name || "NA"}</span>
            ),
            sortable: true,
            minWidth: "200px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Category",
            selector: (row) => row.user_course?.course_category?.name || "NA",
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Language",
            selector: (row) => row.user_course?.language || "NA",
            sortable: true,
            minWidth: "120px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Purchase Date",
            selector: (row) => row.purchage_date ? moment(row.purchage_date).format("DD-MMM-YYYY") : "NA",
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Completion Date",
            selector: (row) => row.completion_date ? moment(row.completion_date).format("DD-MMM-YYYY") : "NA",
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Expiry Status",
            selector: (row) => (
                <span className={`badge ${row.is_expired ? 'bg-danger' : 'bg-success'}`}>
                    {row.is_expired ? 'Expired' : 'Active'}
                </span>
            ),
            sortable: true,
            minWidth: "120px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Course Status",
            selector: (row) => (
                <span className={`badge ${row.is_test_completed ? 'bg-success' : 'bg-warning'}`}>
                    {row.is_test_completed ? 'Completed' : 'In Progress'}
                </span>
            ),
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Certificate",
            selector: (row) => (
                row.certificate ?
                    <a href={row.certificate} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                        View
                    </a> :
                    <span className="badge bg-secondary">Not Available</span>
            ),
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "View Course",
            selector: (row) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <button className="btn" onClick={() => handleViewCourse(row)}>
                        <i
                            className="fe-eye font-22 avatar-title text-primary"
                            style={{ color: "blue" }}
                        />
                    </button>
                </div>
            ),
            minWidth: "120px",
            style: {
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
            },
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headCells: {
            style: {
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                justifyContent: "center",
            },
        },
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    // Handler for completion filter change
    const handleCompletionFilterChange = (event) => {
        setCompletionFilter(event.target.value);
        // Reset pagination to first page when changing filters
        setPage(1);
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate && moment(startDate).isBefore(endDate)) {
            dispatch(
                userCourseList_({
                    id: userId,
                    is_random_test: false,
                    period: "custom",
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    page,
                    limit,
                    is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined
                })
            );
        } else {
            Swal.fire({
                title: 'Invalid Dates',
                text: 'Please select valid start and end dates.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleExportData = useCallback(async () => {
        try {
            setIsExporting(true);

            // Fetch all data for export (you can adjust parameters as needed)
            await dispatch(userCourseList_({
                id: userId,
                is_random_test: false,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                is_report: 1,
                is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined
            }))
                .then((response) => {
                    if (response.payload && response.payload.data) {
                        // Map the data to the format we want for Excel
                        const exportData = response.payload.data.data.map((row, index) => ({
                            "Sr No": index + 1,
                            "Course ID": row.course_id || "NA",
                            "Course Name": row.user_course?.name || "NA",
                            "Category": row.user_course?.course_category?.name || "NA",
                            "Language": row.user_course?.language || "NA",
                            "Purchase Date": row.purchage_date
                                ? moment(row.purchage_date).format("DD-MMM-YYYY")
                                : "NA",
                            "Completion Date": row.completion_date
                                ? moment(row.completion_date).format("DD-MMM-YYYY")
                                : "NA",
                            "Expiry Status": row.is_expired ? "Expired" : "Active",
                            "Course Status": row.is_test_completed ? "Completed" : "In Progress",
                            "Certificate": row.certificate ? "Available" : "Not Available"
                        }));

                        // Create summary data
                        const completedCount = response.payload.data.data.filter(course => course.is_test_completed).length || 0;
                        const notCompletedCount = response.payload.data.length - completedCount;
                        const totalCourses = response.payload.data.length;

                        const summaryData = [
                            {
                                "Report Details": "User Course Summary",
                                "Value": ""
                            },
                            {
                                "Report Details": "Total Courses",
                                "Value": totalCourses
                            },
                            {
                                "Report Details": "Completed",
                                "Value": completedCount
                            },
                            {
                                "Report Details": "In Progress",
                                "Value": notCompletedCount
                            },
                            {
                                "Report Details": "Export Date",
                                "Value": moment().format("DD-MMM-YYYY HH:mm:ss")
                            }
                        ];

                        // Create workbook and worksheets
                        const workbook = XLSX.utils.book_new();

                        // Add summary sheet
                        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
                        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

                        // Add course data sheet
                        const coursesSheet = XLSX.utils.json_to_sheet(exportData);
                        XLSX.utils.book_append_sheet(workbook, coursesSheet, "Course Data");

                        // Save the file
                        const fileName = `User_Courses_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

                        Swal.fire({
                            icon: "success",
                            title: "Export Successful",
                            text: `Data exported to ${fileName}`,
                        });

                        // Refresh the data
                        dispatch(userCourseList_({
                            id: userId,
                            is_random_test: false,
                            period: filter,
                            startDate,
                            endDate,
                            page,
                            limit,
                            is_test_completed: completionFilter !== "" ? completionFilter === "completed" : undefined
                        }));
                    } else {
                        throw new Error("No data available for export");
                    }
                })
                .finally(() => {
                    setIsExporting(false);
                });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.message || "Unable to export user course data",
            });
            setIsExporting(false);
        }
    }, [dispatch, userId, filter, startDate, endDate, page, limit, completionFilter]);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="">
            <div className="col-md-12 col-xl-12 mt-1">
                <div className="widget-rounded-circle card">
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col d-flex justify-content-between align-items-center">
                                <h4 className="header-title">User Course Report</h4>
                                <button
                                    onClick={handleExportData}
                                    className="btn btn-outline-warning d-flex align-items-center gap-2 text-black"
                                    disabled={isExporting}
                                >
                                    {isExporting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                src="/assets/images/download.svg"
                                                alt="Download"
                                                style={{ width: "16px", height: "16px", marginRight: "6px" }}
                                                className='text-warning'
                                            />
                                            Generate Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-3 mb-3">
    <div className="d-flex align-items-end gap-3 flex-wrap">
        {/* Time Period Filter */}
        <div>
            <label className="form-label">Time Period</label>
            <select
                className="form-select"
                onChange={handleFilterChange}
                value={filter}
                style={{ minWidth: "150px" }}
            >
                <option value="">Select</option>
                <option value="day">Today</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
                <option value="custom">Custom</option>
            </select>
        </div>

        {/* Completion Status Filter */}
        <div>
            <label className="form-label">Course Status</label>
            <select
                className="form-select"
                onChange={handleCompletionFilterChange}
                value={completionFilter}
                style={{ minWidth: "150px" }}
            >
                <option value="">All Courses</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
            </select>
        </div>
    </div>

    {/* Custom Date Range (shown only when filter is "custom") */}
    {filter === "custom" && (
        <div className="row mt-3">
            <div className="col">
                <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Start Date"
                    className="form-control"
                />
            </div>
            <div className="col">
                <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    placeholderText="End Date"
                    className="form-control"
                />
            </div>
            <div className="col-auto d-flex align-items-center">
                <button className="btn btn-primary w-100" onClick={handleCustomDateChange}>
                    Apply
                </button>
            </div>
        </div>
    )}
</div>

                        <div className="row mb-4">
                            <div className="col-md-4">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h5 className="card-title text-white">Total Courses</h5>
                                        <h2 className="mt-3 text-white">{totalCourses}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <DataTable
                                title={null}
                                columns={columns}
                                data={userCourseList?.data || []}
                                pagination
                                paginationServer
                                paginationTotalRows={userCourseList?.paging?.total}
                                paginationDefaultPage={page}
                                paginationPerPage={limit}
                                paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                onChangeRowsPerPage={(newrow) => setLimit(newrow)}
                                onChangePage={(newPage) => setPage(newPage)}
                                responsive
                                selectableRowsHighlight
                                highlightOnHover
                                striped
                                fixedHeader
                                persistTableHead
                                noDataComponent={<h5 className="p-3">No Course Data Found</h5>}
                                customStyles={customStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserCourses;